import { Card, LoadingOverlay, Stack, Title } from '@mantine/core'
import { FormattedMessage, useIntl } from 'react-intl'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import { useHasPermissions } from '@/permissions/useHasPermissions'
import { useUpdateWorkspace } from '@/queries/workspaceQueries'
import { useSession } from '@/session/SessionContext'
import { showToast } from '@/theme/notifications'
import { CrudAction, Resource } from '@/types/permissions'
import { useWorkspace } from './WorkspaceContext'
import {
  SubmitValues,
  WorkspaceSettingsForm
} from './WorkspaceSettings/WorkspaceSettingsForm'

export const WorkspaceSettingsPage = () => {
  const intl = useIntl()

  const { workspace } = useWorkspace()
  const { refreshSession } = useSession()

  const { mutateAsync: updateWorkspace, isPending } = useUpdateWorkspace()

  const [canUpdateWorkspace] = useHasPermissions(Resource.Workspaces, [
    CrudAction.Update
  ])

  const handleSubmit = async (formValues: SubmitValues) => {
    try {
      await updateWorkspace({
        workspaceId: workspace.id,
        payload: formValues
      })

      showToast(
        intl.formatMessage({ id: 'workspace.update.success' }),
        'success'
      )

      void refreshSession()
    } catch (err) {
      showToast(
        extractErrorMessage(
          err,
          intl.formatMessage({ id: 'workspace.update.error' })
        ),
        'error'
      )
    }
  }

  return (
    <Stack gap="xs">
      <Title order={3}>
        <FormattedMessage id="workspace.settings" />
      </Title>

      <Card pos="relative" padding="xl" withBorder>
        <LoadingOverlay visible={isPending} />

        <WorkspaceSettingsForm
          disabled={!canUpdateWorkspace}
          mode="update"
          buttonAlign="start"
          workspace={workspace}
          onSubmit={(formValues) => void handleSubmit(formValues)}
        />
      </Card>
    </Stack>
  )
}
